/**
 * Chat Window - EdgeStream Integration
 *
 * Replaces the direct SignalR approach with EdgeStream pipeline
 * - Messages flow through normalization, validation, logging
 * - Full audit trail of all message activities
 * - Configurable logging and persistence
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/useToast';
import { EdgeStreamHubAdapter } from '../adapters/EdgeStreamHubAdapter';
import { getLoggingService, getLogSettings, type IEnvelope } from 'edge-stream-js';
import {
  ChatIcon as ChatBubbleIcon,
  SendIcon,
  FullscreenIcon,
  ExitFullscreenIcon,
  BarChartIcon,
  TrendingUpIcon,
  MailboxIcon as MailboxEmptyIcon,
  LightbulbIcon
} from '@bizfirst/common-react';
import './ChatWindow.css';

// Local utility component
function StatusDot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: color, marginRight: '6px', flexShrink: 0 }} />;
}

interface Message {
  id: string;
  author: string;
  text: string;
  timestamp: Date;
  isOwn: boolean;
}

export interface ChatWindowEdgeStreamProps {
  /** SignalR hub URL (e.g. 'https://localhost:10001/hubs/edge-stream') */
  hubUrl: string;
  serverId: string;
  groupName: string;
  currentUser?: string;
}

export function ChatWindowEdgeStream({
  hubUrl,
  serverId,
  groupName,
  currentUser = 'Inspector User',
}: ChatWindowEdgeStreamProps) {
  const toast = useToast();
  const adapter = useRef<EdgeStreamHubAdapter | null>(null);

  // UI State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [username, setUsername] = useState(currentUser);
  const [isUsernameSet, setIsUsernameSet] = useState(!!currentUser);
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isInGroup, setIsInGroup] = useState(false);

  // Logging state
  const [loggingEnabled, setLoggingEnabled] = useState(getLogSettings().isHookLoggingEnabled());
  const [showLogsLink, setShowLogsLink] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loggingService = getLoggingService();

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Initialize EdgeStream adapter and connect
   */
  useEffect(() => {
    if (!isUsernameSet) return;

    const initAdapter = async () => {
      try {
        // Create adapter
        adapter.current = new EdgeStreamHubAdapter({
          hubUrl,
          serverId,
          enableLogging: loggingEnabled,
        });

        // Connect to hub
        await adapter.current.connect();
        setIsConnected(true);

        // Join server monitoring
        await adapter.current.joinServer();

        // Join group
        await adapter.current.joinGroup(groupName);
        setIsInGroup(true);

        // Subscribe to chat messages through EdgeStream
        adapter.current.subscribe(`group:${serverId}:${groupName}`, handleMessageReceived);

        toast.success(`Connected to ${groupName}`);
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        toast.error(`Failed to connect: ${msg}`);
        console.error('[ChatWindowEdgeStream] Connection error:', error);
      }
    };

    initAdapter();

    return () => {
      // Cleanup on unmount
      if (adapter.current) {
        adapter.current.disconnect().catch(console.error);
      }
    };
  }, [isUsernameSet, hubUrl, serverId, groupName, loggingEnabled]);

  /**
   * Handle message received from EdgeStream pipeline
   * Message has already been processed through hooks and logging
   */
  const handleMessageReceived = useCallback((envelope: IEnvelope) => {
    try {
      // Extract business data from normalized envelope
      const envelopeBody = envelope.body as any;
      const businessData = envelopeBody.data?.data || envelopeBody.data || {};

      // Create message
      const newMessage: Message = {
        id: (businessData.messageId || envelope.meta.id) as string,
        author: (businessData.author as string) || 'Unknown',
        text: (businessData.text as string) || '',
        timestamp: new Date(businessData.timestamp as string),
        isOwn: false,
      };

      // Avoid duplicate messages
      setMessages((prev) => {
        if (prev.find((m) => m.id === newMessage.id)) return prev;
        return [...prev, newMessage];
      });

      // Log in console if needed
      if (loggingEnabled) {
        console.log('[ChatWindowEdgeStream] Message delivered:', {
          messageId: newMessage.id,
          author: newMessage.author,
          text: newMessage.text,
          envelope: envelope,
        });
      }
    } catch (error) {
      console.error('[ChatWindowEdgeStream] Error processing message:', error);
      toast.error('Error processing message');
    }
  }, [loggingEnabled]);

  /**
   * Send message through EdgeStream
   */
  const handleSendMessage = async () => {
    if (!inputText.trim() || !isUsernameSet || !adapter.current) return;

    setIsSending(true);
    const messageText = inputText;
    const messageTimestamp = new Date();
    const messageId = `msg-${Date.now()}-${Math.random()}`;

    // Optimistic UI update
    const optimisticMessage: Message = {
      id: messageId,
      author: username,
      text: messageText,
      timestamp: messageTimestamp,
      isOwn: true,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setInputText('');

    try {
      // Send via EdgeStream adapter
      const success = await adapter.current.sendMessage(groupName, username, messageText);

      if (!success) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        toast.error('Failed to send message');
      } else {
        // Log message sent
        if (loggingEnabled) {
          console.log('[ChatWindowEdgeStream] Message sent:', {
            messageId,
            author: username,
            text: messageText,
          });
        }
      }
    } catch (error) {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.error('Error sending message');
      console.error('[ChatWindowEdgeStream] Send error:', error);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Toggle logging
   */
  const handleLoggingToggle = (enabled: boolean) => {
    setLoggingEnabled(enabled);
    getLogSettings().setHookLoggingEnabled(enabled);
    if (adapter.current) {
      adapter.current.setLoggingEnabled(enabled);
    }
  };

  // Fullscreen view
  if (isFullscreen && isUsernameSet) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-dark)',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        {/* Fullscreen Header */}
        <div
          style={{
            padding: '12px 20px',
            background: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div>
            <h2 style={{ margin: '0 0 4px 0', color: 'var(--primary)', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ChatBubbleIcon /> {groupName}
            </h2>
            <p style={{ margin: '0', color: 'var(--text-secondary)', fontSize: '12px' }}>
              Server: {serverId} • User: {username} • {messages.length} messages
            </p>
          </div>
          <button
            onClick={() => setIsFullscreen(false)}
            className="button button-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              padding: '6px 12px',
            }}
          >
            <ExitFullscreenIcon /> Exit Fullscreen
          </button>
        </div>

        {/* Fullscreen Chat Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Messages Area */}
          <div className="chat-messages" style={{ flex: 1, overflow: 'auto' }}>
            {messages.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`chat-message ${msg.isOwn ? 'own' : ''}`}
                  style={{
                    padding: '12px 16px',
                    marginBottom: '8px',
                    background: msg.isOwn ? 'rgba(0, 212, 255, 0.1)' : 'var(--bg-secondary)',
                    borderLeft: msg.isOwn ? '3px solid var(--primary)' : '3px solid var(--border)',
                    marginLeft: msg.isOwn ? '40px' : '0',
                    marginRight: msg.isOwn ? '0' : '40px',
                    borderRadius: '6px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <strong>{msg.author}</strong>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {msg.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <div>{msg.text}</div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type your message..."
              className="input"
              style={{ flex: 1 }}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim() || isSending || !isConnected}
              className="button button-primary"
              style={{ minWidth: '80px' }}
            >
              <SendIcon /> {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isUsernameSet) {
    return (
      <div className="chat-setup">
        <div className="chat-setup-card">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><ChatBubbleIcon /> Join Chat (EdgeStream)</h2>
          <p className="group-info">
            Group: <strong>{groupName}</strong> on <strong>{serverId}</strong>
          </p>
          <p>Set your username to start chatting in this group</p>

          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your username"
            className="input"
            onKeyPress={(e) => e.key === 'Enter' && setIsUsernameSet(true)}
            autoFocus
          />

          <button
            className="button button-primary button-large"
            onClick={() => setIsUsernameSet(true)}
            disabled={!username.trim()}
          >
            Start Chatting
          </button>

          <div className="chat-info">
            <h3>EdgeStream Integration</h3>
            <ul>
              <li>Messages flow through EdgeStream pipeline</li>
              <li>Full normalization to CloudEvents standard</li>
              <li>Complete audit trail via logging service</li>
              <li>Real-time delivery via SignalR hub</li>
              <li>Optional message body & metadata logging</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><ChatBubbleIcon /> {groupName}</h3>
          <p className="chat-group-info">
            Server: {serverId} • User: {username}
            {isConnected && isInGroup ? (
              <span style={{ marginLeft: '12px', color: '#4ade80', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}>
                <StatusDot color="#4ade80" /> Connected (EdgeStream)
              </span>
            ) : isConnected && !isInGroup ? (
              <span style={{ marginLeft: '12px', color: '#facc15', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}>
                <StatusDot color="#facc15" /> Connected (NOT in group)
              </span>
            ) : (
              <span style={{ marginLeft: '12px', color: '#f87171', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}>
                <StatusDot color="#f87171" /> Connecting...
              </span>
            )}
          </p>
        </div>
        <div className="chat-header-actions">
          <span className="message-count">{messages.length} messages</span>

          {/* Logging Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="logging"
              checked={loggingEnabled}
              onChange={(e) => handleLoggingToggle(e.target.checked)}
              title="Enable/disable message logging"
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="logging" style={{ fontSize: '12px', cursor: 'pointer', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <BarChartIcon /> Log
            </label>
          </div>

          {/* Show logs link if logging enabled */}
          {loggingEnabled && loggingService.getTotalLogCount() > 0 && (
            <button
              className="btn-standard btn-small"
              onClick={() => setShowLogsLink(!showLogsLink)}
              title="View message logs"
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              <TrendingUpIcon /> {loggingService.getTotalLogCount()} logs
            </button>
          )}

          <button
            className="btn-standard btn-small"
            onClick={() => setIsUsernameSet(false)}
            title="Change username"
          >
            Switch User
          </button>

          <button
            className="btn-standard btn-small"
            onClick={() => setIsFullscreen(true)}
            title="Toggle fullscreen mode"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <FullscreenIcon />
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p><MailboxEmptyIcon /> No messages yet. Start a conversation!</p>
            {loggingEnabled && (
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <LightbulbIcon /> Logging enabled - check Inspector Logs page to view pipeline activity
              </p>
            )}
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`chat-message ${msg.isOwn ? 'own' : 'other'}`}>
              <div className="message-header">
                <span className="message-author">{msg.author}</span>
                <span className="message-time">{msg.timestamp.toLocaleTimeString()}</span>
              </div>
              <div className="message-text">{msg.text}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Logs Info Panel */}
      {showLogsLink && loggingEnabled && (
        <div
          style={{
            padding: '12px',
            background: 'rgba(0, 212, 255, 0.1)',
            borderTop: '1px solid rgba(0, 212, 255, 0.3)',
            fontSize: '12px',
            color: 'var(--text-secondary)',
          }}
        >
          <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><BarChartIcon /> Pipeline Activity:</strong> {loggingService.getTotalLogCount()} messages logged
          <br />
          View detailed logs in the Inspector's Logs page (LogsPage)
        </div>
      )}

      <div className="chat-input">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isSending) handleSendMessage();
          }}
          placeholder="Type a message..."
          className="input"
          disabled={isSending}
        />
        <button
          className="button button-primary"
          onClick={handleSendMessage}
          disabled={!inputText.trim() || isSending}
        >
          {isSending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
}
